import classNames from 'classnames';
import React, { ComponentProps, forwardRef, FunctionComponent } from 'react';
import { DisplayAmount } from './constants';

/**
 * Renders content wrapped in the necessary divs to support collapse/expand
 * behavior but does not implement any of the logic to determine if the content
 * should be collapsed or not.
 */
export const ReadMoreBase = forwardRef<
    HTMLDivElement,
    ComponentProps<
        FunctionComponent<{
            /**
             * Additional CSS classes to add to the root container.
             */
            className?: string;
            /**
             * The amount of the content to display when in collapsed state.
             **/
            collapsedDisplayAmount: DisplayAmount;
            /**
             * Whether content is currently expanded or not.
             */
            isExpanded: boolean;
            /**
             * `true` when content is big enough to require truncation.
             *
             * @note This prop is not telling if the content is expanded or
             * collapsed. Use `isExpanded` for that.
             */
            shouldTruncateContent: boolean;
        }>
    >
>(
    (
        {
            children,
            className,
            collapsedDisplayAmount,
            isExpanded,
            shouldTruncateContent,
        },
        contentDivRef,
    ) => {
        const classes = shouldTruncateContent
            ? classNames('eds-read-more', className, {
                  'eds-read-more--none':
                      collapsedDisplayAmount === DisplayAmount.none,
                  'eds-read-more--short':
                      collapsedDisplayAmount === DisplayAmount.small,
                  'eds-read-more--intermediate':
                      collapsedDisplayAmount === DisplayAmount.medium,
                  'eds-read-more--tall':
                      collapsedDisplayAmount === DisplayAmount.large,
                  'eds-read-more--collapsed': !isExpanded,
              })
            : classNames('eds-read-more');

        return (
            <div className={classes} ref={contentDivRef}>
                <div
                    data-testid="eds-read-more"
                    className="eds-read-more__content"
                >
                    {children}
                </div>
            </div>
        );
    },
);
